import { FormData, AnnualIncomeTableRow, AnnualMetrics, TaxBrackets } from '../Models/disability-calculator';
import {formatCurrencyInteger, toNumber, parseUTCDate, generateTaxBrackets, parsePercentage, debug, roundTo5Decimals} from '../utils/utils';
import { generateUnifiedAnnualTable } from '../utils/unifiedTable';

export function getAnnualIncomeTableData(formData: FormData): AnnualIncomeTableRow[] {
  debug('Starting getAnnualIncomeTableData');
  const { employmentInfo } = formData;
  const currentDisabilityYear = parseUTCDate(employmentInfo.dateOfDisability).getFullYear();
  const annualUnifiedTable = generateUnifiedAnnualTable(formData);

  debug('currentDisabilityYear:', currentDisabilityYear);
  debug('annualUnifiedTable:', annualUnifiedTable);

  const result = annualUnifiedTable.map((yearData, index) => {
    const year = (currentDisabilityYear + index).toString();
    const annualMetricsForYear = Object.values(yearData)[0] as AnnualMetrics;

    debug(`Processing year: ${year}`);
    debug('annualMetricsForYear:', annualMetricsForYear);

    const taxableWages = calculateTaxableWages(annualMetricsForYear);
    const postDedTax = taxableWages + annualMetricsForYear.ee401kPTAX;
    const totalTaxes = calculateTotalTaxes(year, taxableWages, postDedTax, annualMetricsForYear, formData);
    const net = calculateNet(totalTaxes, postDedTax, annualMetricsForYear);
    const stdLtd = annualMetricsForYear.totalltdstd;
    const netNet = Math.ceil(net + stdLtd);

    debug(`Year ${year} calculations:`, {
      taxableWages,
      postDedTax,
      totalTaxes,
      net,
      stdLtd,
      netNet
    });

    return {
      year,
      deferrableWage: formatCurrencyInteger(annualMetricsForYear.deferrableWage),
      taxableWage: formatCurrencyInteger(taxableWages),
      totalTax: formatCurrencyInteger(totalTaxes),
      net: formatCurrencyInteger(net),
      stdLtd: formatCurrencyInteger(stdLtd),
      nonTaxLol: formatCurrencyInteger(annualMetricsForYear.nonTaxableLOL),
      netNet: formatCurrencyInteger(netNet),
    };
  });

  debug('Completed getAnnualIncomeTableData, result:', result);
  return result;
}

function calculateTaxableWages(annualMetricsForYear: AnnualMetrics): number {
  const result = annualMetricsForYear.deferrableWage + 
         annualMetricsForYear.taxableCashAward + 
         annualMetricsForYear.taxableOverage + 
         annualMetricsForYear.taxableExcess;
  debug('calculateTaxableWages:', {
    deferrableWage: annualMetricsForYear.deferrableWage,
    taxableCashAward: annualMetricsForYear.taxableCashAward,
    taxableOverage: annualMetricsForYear.taxableOverage,
    taxableExcess: annualMetricsForYear.taxableExcess,
    result
  });
  return result;
}

function calculateTotalTaxes(year: string, taxableWages: number, postDedTax: number, annualMetricsForYear: AnnualMetrics, formData: FormData): number {
  debug('Starting calculateTotalTaxes:', { year, taxableWages, postDedTax });
  // const endYear = parseUTCDate(formData.employmentInfo.dateOfRetirement).getFullYear();
  // const taxBrackets = formData.assumptions.TaxBrackets;
  // const fedTax = calculateFedTax(year, endYear, postDedTax, taxBrackets);
  const fedTaxPct = parsePercentage(formData.financialInfo.federalTaxBracket);
  const fedTax = -roundTo5Decimals(postDedTax * fedTaxPct);
  const stateTaxPct = parsePercentage(formData.financialInfo.stateTaxBracket);
  const stateTax = -roundTo5Decimals(postDedTax * stateTaxPct);
  const ssFicaTax = calculateSsFicaTax(taxableWages, annualMetricsForYear.ssWage, parsePercentage(formData.assumptions["SSPect"]));
  const medFicaTax = -roundTo5Decimals(taxableWages * parsePercentage(formData.assumptions["MED"]));

  const result = roundTo5Decimals(fedTax + stateTax + ssFicaTax + medFicaTax);
  debug('Completed calculateTotalTaxes:', { fedTax, stateTax, ssFicaTax, medFicaTax, result });
  return result;
}

// Holding Function for Future Use
function calculateFedTax(year: string, endYear: number, postDedTax: number, taxBracket: TaxBrackets): number {
  debug('Starting calculateFedTax:', { year, endYear, postDedTax });
  const taxBrackets = generateTaxBrackets(endYear, taxBracket);
  const brackets = taxBrackets[parseInt(year)];
  const percentage = [0.1, 0.12, 0.22, 0.24, 0.32, 0.35, 0.37];

  if (!brackets) {
    throw new Error(`No tax brackets found for year ${year}`);
  }

  let applicableRate = null;
  let applicablePercentage = 1;
  for (let i = 0; i < brackets.length; i++) {
    if (postDedTax >= brackets[i]) {
      applicableRate = brackets[i];
      applicablePercentage = percentage[i];
    } else {
      break;
    }
  }

  const result = applicableRate === null ? 0 : -roundTo5Decimals(postDedTax * applicablePercentage);
  debug('Completed calculateFedTax:', { applicableRate, applicablePercentage, result });
  return result;
}

function calculateSsFicaTax(taxableWage: number, ssWage: number, ssPect: number): number {
  const result = -roundTo5Decimals((taxableWage > ssWage ? ssWage : taxableWage) * ssPect);
  debug('calculateSsFicaTax:', { taxableWage, ssWage, ssPect, result });
  return result;
}

function calculateNet(totalTaxes: number, postDedTax: number, annualMetricsForYear: AnnualMetrics): number {
  const unionDues = -roundTo5Decimals((annualMetricsForYear.taxableSickPay + annualMetricsForYear.taxableVacationPay) * 0.01);
  const result = roundTo5Decimals(annualMetricsForYear.nonTaxableLOL + postDedTax + totalTaxes + unionDues);
  debug('calculateNet:', { 
    totalTaxes, 
    postDedTax, 
    taxableSickPay: annualMetricsForYear.taxableSickPay,
    taxableVacationPay: annualMetricsForYear.taxableVacationPay,
    unionDues, 
    nonTaxableLOL: annualMetricsForYear.nonTaxableLOL,
    result 
  });
  return result;
}
